/**
 * API client for the RallyShift backend.
 *
 * Every request carries the tenant, either implicitly through the subdomain in
 * production or explicitly through X-Organization-Slug during development.
 */

import { customDomainHost, getOrgSlug } from './tenant';
import type { EventInput } from '../types';

function publicApiOrigin(): string {
  let url = (process.env.REACT_APP_API_URL || 'http://localhost:4000').replace(/\/$/, '');
  // Same mixed-content trap as the marketing site: an https tenant page cannot
  // call an http API host. Upgrade only when the page itself is already TLS.
  if (typeof window !== 'undefined' && window.location.protocol === 'https:' && url.startsWith('http://')) {
    url = `https://${url.slice('http://'.length)}`;
  }
  return url;
}

const API_URL = publicApiOrigin();

const TOKEN_KEY = 'winterevent_token';

function tenantHeaders(): Record<string, string> {
  const headers: Record<string, string> = {};
  const host = customDomainHost();
  if (host) headers['X-Organization-Host'] = host;
  const slug = getOrgSlug();
  if (slug) headers['X-Organization-Slug'] = slug;
  return headers;
}

function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

export function removeToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  options?: { skipAuth?: boolean }
): Promise<T> {
  const url = `${API_URL}/api${path}`;
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...tenantHeaders(),
  };
  if (!options?.skipAuth) {
    const token = getToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;
  }
  const res = await fetch(url, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
    credentials: 'include',
  });
  if (!res.ok) {
    const errBody = await res.json().catch(() => ({}));
    const err = new Error((errBody as any).message || errBody?.error || res.statusText || `HTTP ${res.status}`);
    (err as any).status = res.status;
    (err as any).body = errBody;
    throw err;
  }
  if (res.status === 204 || res.headers.get('content-length') === '0') {
    return undefined as T;
  }
  return res.json();
}

async function uploadFile<T>(
  path: string,
  file: File,
  field = 'avatar',
): Promise<T> {
  const url = `${API_URL}/api${path}`;
  const formData = new FormData();
  formData.append(field, file);
  const headers: Record<string, string> = { ...tenantHeaders() };
  const token = getToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(url, {
    method: 'POST',
    headers,
    body: formData,
    credentials: 'include',
  });
  if (!res.ok) {
    const errBody = await res.json().catch(() => ({}));
    const err = new Error((errBody as any).message || errBody?.error || res.statusText || `HTTP ${res.status}`);
    (err as any).status = res.status;
    (err as any).body = errBody;
    throw err;
  }
  return res.json();
}

async function download(path: string, fallbackName: string): Promise<void> {
  const url = `${API_URL}/api${path}`;
  const headers: Record<string, string> = { ...tenantHeaders() };
  const token = getToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(url, { headers, credentials: 'include' });
  if (!res.ok) {
    const errBody = await res.json().catch(() => ({}));
    const err = new Error(
      (errBody as any).message || errBody?.error || res.statusText || `HTTP ${res.status}`,
    );
    (err as any).status = res.status;
    throw err;
  }

  const blob = await res.blob();
  const disposition = res.headers.get('content-disposition') || '';
  const named = /filename="?([^"]+)"?/.exec(disposition);
  const filename = named?.[1] || fallbackName;

  const href = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = href;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(href);
}

export const api = {
  auth: {
    getSession: () => request<any>('GET', '/auth/session'),
    login: (email: string, password: string) =>
      request<{ token: string; user: any }>('POST', '/auth/login', { email, password }, { skipAuth: true }),
    register: (data: { email: string; password: string; firstName: string; lastName: string; phoneNumber?: string }) =>
      request<{ message?: string; token?: string; user: any }>('POST', '/auth/register', {
        email: data.email,
        password: data.password,
        firstName: data.firstName,
        lastName: data.lastName,
        phoneNumber: data.phoneNumber,
      }, { skipAuth: true }),
    confirmEmail: (token: string) =>
      request<{ success: boolean; message: string }>('GET', `/auth/confirm-email?token=${encodeURIComponent(token)}`, undefined, { skipAuth: true }),
    logout: () => {
      removeToken();
    },
    forgotPassword: (email: string) =>
      request('POST', '/auth/forgot-password', { email }, { skipAuth: true }),
    resetPassword: (token: string, newPassword: string) =>
      request('POST', '/auth/reset-password', { token, newPassword }, { skipAuth: true }),
    updatePassword: (newPassword: string) =>
      request('POST', '/auth/update-password', { newPassword }),
    createVolunteer: (data: { email: string; password: string; firstName: string; lastName: string; phoneNumber?: string; skills?: string[] }) =>
      request('POST', '/auth/admin/volunteers', {
        email: data.email,
        password: data.password,
        firstName: data.firstName,
        lastName: data.lastName,
        phoneNumber: data.phoneNumber,
        skills: data.skills,
      }),
    sendPasswordReset: (userId: string) =>
      request<{ message: string; reset_url?: string }>('POST', '/auth/admin/password-reset', {
        user_id: userId,
      }),
    sendPasswordResetBatch: (userIds: string[]) =>
      request<{
        sent: number;
        skipped: number;
        reset_urls?: { email: string; reset_url: string }[];
      }>('POST', '/auth/admin/password-reset/batch', { user_ids: userIds }),
  },
  profiles: {
    getMe: () => request<any>('GET', '/profiles/me'),
    get: (id: string) => request<any>('GET', `/profiles/${id}`),
    listAdmins: () => request<any[]>('GET', '/profiles/admins'),
    updateMe: (data: any) => request<any>('PATCH', '/profiles/me', data),
    uploadAvatar: (file: File) => uploadFile<{ avatar_url: string }>('/profiles/me/avatar', file),
    list: () => request<any[]>('GET', '/profiles'),
    update: (id: string, data: any) => request<any>('PATCH', `/profiles/${id}`, data),
    delete: (id: string) => request('DELETE', `/profiles/${id}`),
    removeBatch: (userIds: string[]) =>
      request<{ removed: number; skipped: number }>('POST', '/profiles/batch-remove', {
        user_ids: userIds,
      }),
  },
  volunteerMetadata: {
    getMe: () => request<any>('GET', '/volunteer-metadata/me'),
    list: () => request<any[]>('GET', '/volunteer-metadata'),
    upsert: (id: string, data: any) => request<any>('PUT', `/volunteer-metadata/${id}`, data),
    delete: (id: string) => request('DELETE', `/volunteer-metadata/${id}`),
  },
  volunteerRequests: {
    listPending: () => request<any[]>('GET', '/volunteer-requests/pending'),
    create: (data: { email: string; first_name?: string; last_name?: string; phone_number?: string; motivation?: string }) =>
      request<{
        id: string;
        user_id: string;
        email: string;
        first_name: string | null;
        last_name: string | null;
        status: 'pending' | 'approved' | 'rejected';
        requested_at: string;
      }>('POST', '/volunteer-requests', data),
    approve: (id: string) => request('PATCH', `/volunteer-requests/${id}/approve`, {}),
    reject: (id: string, notes?: string) => request('PATCH', `/volunteer-requests/${id}/reject`, { notes }),
  },
  events: {
    list: () => request<any[]>('GET', '/events'),
    get: (id: string) => request<any>('GET', `/events/${id}`),
    create: (data: EventInput) => request<any>('POST', '/events', data),
    update: (id: string, data: Partial<EventInput> & { current_capacity?: number }) =>
      request<any>('PATCH', `/events/${id}`, data),
    /** Rejected with 409 while the event still has shifts hanging off it. */
    delete: (id: string) => request<void>('DELETE', `/events/${id}`),
  },
  shifts: {
    list: (status?: string) =>
      request<any[]>( 'GET', status ? `/shifts?status=${encodeURIComponent(status)}` : '/shifts'),
    getAssignmentCounts: () => request<Record<string, number>>('GET', '/shifts/assignments/counts'),
    getPendingAssignments: () => request<any[]>('GET', '/shifts/assignments/pending'),
    get: (id: string) => request<any>('GET', `/shifts/${id}`),
    getAssignments: (shiftId: string) => request<any[]>('GET', `/shifts/${shiftId}/assignments`),
    getMyAssignments: () => request<any[]>('GET', '/shifts/assignments/me'),
    getAssignmentsByVolunteer: (volunteerId: string) =>
      request<any[]>('GET', `/shifts/assignments/by-volunteer/${volunteerId}`),
    create: (data: any) => request<any>('POST', '/shifts', data),
    update: (id: string, data: any) => request<any>('PATCH', `/shifts/${id}`, data),
    delete: (id: string) => request('DELETE', `/shifts/${id}`),
    upsertAssignment: (shiftId: string, volunteerId?: string, status?: string) =>
      request<{ id: string }>('POST', `/shifts/${shiftId}/assignments`, {
        volunteer_id: volunteerId,
        status: status || 'assigned',
      }),
    updateAssignment: (assignmentId: string, data: { status?: string; notes?: string }) =>
      request<any>('PATCH', `/shifts/assignments/${assignmentId}`, data),
    getDayRoster: (date: string) =>
      request<any[]>('GET', `/shifts/assignments/day?date=${encodeURIComponent(date)}`),
    setAttendance: (assignmentId: string, attendance: string) =>
      request<any>('PATCH', `/shifts/assignments/${assignmentId}/attendance`, { attendance }),
  },
  organizations: {
    current: () =>
      request<{
        id: string;
        name: string;
        slug: string;
        logo_url: string | null;
        brand_color: string;
        on_brand_color: string;
        locale: string;
        brand_contrast_ok: boolean;
        allow_public_signup: boolean;
        volunteer_check_in_enabled: boolean;
      }>('GET', '/organizations/current', undefined, { skipAuth: true }),
    /** Reveals only whether a name is free, which visiting the subdomain already shows. */
    slugAvailable: (slug: string) =>
      request<{ slug: string; valid: boolean; available: boolean }>(
        'GET',
        `/organizations/slug-available?slug=${encodeURIComponent(slug)}`,
        undefined,
        { skipAuth: true },
      ),
    myMemberships: () => request<any[]>('GET', '/organizations/me/memberships'),
    update: (data: {
      name?: string;
      logo_url?: string | null;
      contact_email?: string | null;
      locale?: string;
      brand_color?: string;
      allow_public_signup?: boolean;
      volunteer_check_in_enabled?: boolean;
    }) => request<any>('PATCH', '/organizations/current', data),
    settings: () =>
      request<{
        id: string;
        name: string;
        slug: string;
        logo_url: string | null;
        brand_color: string;
        on_brand_color: string;
        locale: string;
        allow_public_signup: boolean;
        volunteer_check_in_enabled: boolean;
        brand_contrast_ok: boolean;
        contact_email: string | null;
        custom_domain: string | null;
      }>('GET', '/organizations/current/settings'),
    subscription: () =>
      request<OrganizationSubscription>('GET', '/organizations/current/subscription'),
    listSkills: () =>
      request<{ id: string; name: string; description: string | null; sort_order: number; archived: boolean }[]>(
        'GET',
        '/organizations/current/skills',
      ),
    createSkill: (data: { name: string; description?: string }) =>
      request<any>('POST', '/organizations/current/skills', data),
    updateSkill: (id: string, data: { name?: string; description?: string; sort_order?: number; archived?: boolean }) =>
      request<any>('PATCH', `/organizations/current/skills/${id}`, data),
    deleteSkill: (id: string) => request('DELETE', `/organizations/current/skills/${id}`),
  },
  invitations: {
    list: () =>
      request<{
        id: string;
        email: string;
        first_name: string | null;
        last_name: string | null;
        role: string;
        skills: string[] | null;
        expires_at: string;
        created_at: string;
      }[]>('GET', '/invitations'),
    // accept_url only comes back when the server has no mail provider configured.
    create: (data: {
      email: string;
      first_name?: string;
      last_name?: string;
      role?: string;
      skills?: string[];
    }) => request<{ id: string; email: string; accept_url?: string }>('POST', '/invitations', data),
    resend: (id: string) =>
      request<{ id: string; email: string; accept_url?: string }>(
        'POST',
        `/invitations/${id}/resend`,
        {},
      ),
    revoke: (id: string) => request('DELETE', `/invitations/${id}`),
    lookup: (token: string) =>
      request<{
        email: string;
        first_name: string | null;
        last_name: string | null;
        role: string;
        skills: string[] | null;
        skill_catalogue: string[];
        expires_at: string;
        needs_account: boolean;
        organization: {
          id: string;
          name: string;
          slug: string;
          logo_url: string | null;
          brand_color: string;
          on_brand_color: string;
        };
      }>('GET', `/invitations/lookup?token=${encodeURIComponent(token)}`, undefined, {
        skipAuth: true,
      }),
    accept: (data: {
      token: string;
      password?: string;
      first_name?: string;
      last_name?: string;
      phone_number?: string;
      skills?: string[];
    }) =>
      request<{ token: string; organization: { id: string; name: string; slug: string } }>(
        'POST',
        '/invitations/accept',
        data,
        { skipAuth: true },
      ),
  },
  messaging: {
    list: () => request<Conversation[]>('GET', '/conversations'),
    unread: () => request<{ count: number }>('GET', '/conversations/unread'),
    get: (id: string) => request<Conversation>('GET', `/conversations/${id}`),
    people: (q?: string) =>
      request<Person[]>('GET', q ? `/conversations/people?q=${encodeURIComponent(q)}` : '/conversations/people'),
    openDirect: (userId: string) => request<Conversation>('POST', '/conversations/direct', { user_id: userId }),
    forShift: (shiftId: string) => request<Conversation>('GET', `/conversations/for-shift/${shiftId}`),
    forEvent: (eventId: string) => request<Conversation>('GET', `/conversations/for-event/${eventId}`),
    forOrganization: () => request<Conversation>('GET', '/conversations/for-organization'),
    messages: (id: string, cursor?: { before?: string; after?: string }) => {
      const params = new URLSearchParams();
      if (cursor?.before) params.set('before', cursor.before);
      if (cursor?.after) params.set('after', cursor.after);
      const query = params.toString();
      return request<{ messages: Message[]; has_more: boolean }>(
        'GET',
        `/conversations/${id}/messages${query ? `?${query}` : ''}`,
      );
    },
    send: (id: string, data: { body?: string; attachment_ids?: string[]; client_id: string; email?: boolean }) =>
      request<Message>('POST', `/conversations/${id}/messages`, data),
    upload: (id: string, file: File) =>
      uploadFile<Attachment>(`/conversations/${id}/attachments`, file, 'file'),
    markRead: (id: string, messageId?: string) =>
      request<void>('POST', `/conversations/${id}/read`, messageId ? { message_id: messageId } : {}),
    setMuted: (id: string, muted: boolean) => request<void>('PATCH', `/conversations/${id}/mute`, { muted }),
    deleteMessage: (messageId: string) => request<void>('DELETE', `/messages/${messageId}`),
    report: (messageId: string, reason?: string) =>
      request<void>('POST', `/messages/${messageId}/report`, reason ? { reason } : {}),
    removeFromChannel: (id: string, userId: string) =>
      request<void>('POST', `/conversations/${id}/removals`, { user_id: userId }),
  },
  adminSettings: {
    getAutoApproval: () => request<{ enabled: boolean }>('GET', '/admin/settings/auto-approve'),
    setAutoApproval: (enabled: boolean) => request('POST', '/admin/settings/auto-approve', { enabled }),
  },
  notifications: {
    send: (type: string, data: Record<string, unknown>) =>
      request('POST', '/notifications/send', { type, data }),
  },
  platform: {
    listOrganizations: () => request<PlatformOrganizationSummary[]>('GET', '/platform/organizations'),
    getOrganization: (id: string) =>
      request<PlatformOrganizationDetail>('GET', `/platform/organizations/${id}`),
    createOrganization: (data: {
      name: string;
      slug: string;
      owner_email: string;
      owner_password?: string;
      owner_first_name?: string;
      owner_last_name?: string;
      plan?: string;
    }) =>
      request<PlatformOrganizationDetail & { owner_existed: boolean }>(
        'POST',
        '/platform/organizations',
        data,
      ),
    updateSubscription: (
      id: string,
      data: {
        plan?: string;
        subscription_status?: string;
        seat_limit?: number | null;
        trial_ends_at?: string | null;
        subscription_started_at?: string | null;
        current_period_start?: string | null;
        current_period_end?: string | null;
        subscription_notes?: string | null;
        status?: string;
      },
    ) => request<PlatformOrganizationDetail>('PATCH', `/platform/organizations/${id}/subscription`, data),
    updateCustomDomain: (id: string, custom_domain: string | null) =>
      request<PlatformOrganizationDetail>('PATCH', `/platform/organizations/${id}/domain`, {
        custom_domain,
      }),
    deleteOrganization: (id: string, confirm_slug: string) =>
      request<{ slug: string; name: string; orphaned_users: number }>(
        'DELETE',
        `/platform/organizations/${id}`,
        { confirm_slug },
      ),
    exportJson: (id: string, slug: string) =>
      download(`/platform/organizations/${id}/export.json`, `${slug}-export.json`),
    exportZip: (id: string, slug: string) =>
      download(`/platform/organizations/${id}/export.zip`, `${slug}-export.zip`),
    importOrganization: (data: { slug: string; name?: string; data: unknown }) =>
      request<{
        organization: { id: string; name: string; slug: string };
        users_created: number;
        users_linked: number;
        rows: Record<string, number>;
      }>('POST', '/platform/organizations/import', data),
    emailStatus: () => request<{ emailConfigured: boolean }>('GET', '/platform/email/status'),
    emailTest: (data: { organization_id: string; to?: string }) =>
      request<{
        ok: boolean;
        id?: string;
        error?: string;
        from: string;
        to: string[];
        mode: 'address' | 'organisation-admins';
      }>('POST', '/platform/email/test', data),
    audit: () =>
      request<
        {
          id: string;
          actor_email: string;
          action: string;
          organization_slug: string | null;
          details: Record<string, unknown>;
          created_at: string;
        }[]
      >('GET', '/platform/audit'),
  },
};

export interface PlatformOrganizationSummary {
  id: string;
  name: string;
  slug: string;
  status: string;
  plan: string;
  subscription_status: string;
  seat_limit: number | null;
  trial_ends_at: string | null;
  current_period_end: string | null;
  contact_email: string | null;
  created_at: string;
  members: number;
  events: number;
  shifts: number;
}

export interface PlatformOrganizationDetail {
  id: string;
  name: string;
  slug: string;
  status: string;
  custom_domain: string | null;
  logo_url: string | null;
  brand_color: string;
  contact_email: string | null;
  locale: string;
  allow_public_signup: boolean;
  volunteer_check_in_enabled: boolean;
  plan: string;
  subscription_status: string;
  seat_limit: number | null;
  trial_ends_at: string | null;
  subscription_started_at: string | null;
  current_period_start: string | null;
  current_period_end: string | null;
  subscription_notes: string | null;
  created_at: string;
}

export interface OrganizationSubscription {
  plan: string;
  subscription_status: string;
  status: string;
  trial_ends_at: string | null;
  subscription_started_at: string | null;
  current_period_start: string | null;
  current_period_end: string | null;
  seat_limit: number | null;
  seats_used: number;
  customer_since: string;
}

export interface Person {
  id: string;
  first_name: string | null;
  last_name: string | null;
  avatar_url: string | null;
  role: string | null;
  is_organiser: boolean;
}

export interface Attachment {
  id: string;
  kind: 'image' | 'file';
  mime: string;
  size: number;
  width: number | null;
  height: number | null;
  name: string | null;
  url: string;
  thumbnail_url: string | null;
}

export interface Message {
  id: string;
  conversation_id: string;
  client_id: string | null;
  body: string;
  created_at: string;
  deleted: boolean;
  sender: Person;
  is_mine: boolean;
  attachments: Attachment[];
  emailed?: boolean;
  report_count?: number;
}

export type ConversationKind = 'organization' | 'event' | 'shift' | 'direct';

export interface Conversation {
  id: string;
  kind: ConversationKind;
  title: string;
  subtitle: string | null;
  event_id: string | null;
  shift_id: string | null;
  starts_at: string | null;
  other_person: Person | null;
  last_message_at: string | null;
  last_message: {
    id: string;
    body: string;
    created_at: string;
    sender_id: string;
    sender_first_name: string | null;
    is_mine: boolean;
    deleted: boolean;
    attachment_kinds: ('image' | 'file')[];
  } | null;
  unread_count: number;
  muted: boolean;
  can_post: boolean;
  can_attach_files: boolean;
  can_moderate: boolean;
}

/** The live-updates socket, authenticated the only way a browser WebSocket can be. */
export function realtimeUrl(): string | null {
  const token = getToken();
  if (!token) return null;
  return `${API_URL.replace(/^http/, 'ws')}/api/realtime?token=${encodeURIComponent(token)}`;
}

export function setAuthToken(token: string): void {
  setToken(token);
}

export default api;
